import { Fragment, type JSX } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import type { IconName } from "@frontend/components/atoms/Icon";

import { Breadcrumb } from "./Breadcrumb";
import {
  type BreadcrumbDataProps,
  breadcrumbDataSchema,
} from "./Breadcrumb.schema";
import { BreadcrumbItem } from "./BreadcrumbItem";
import { BreadcrumbLink } from "./BreadcrumbLink";
import { BreadcrumbList } from "./BreadcrumbList";
import { BreadcrumbSeparator } from "./BreadcrumbSeparator";
import { BREADCRUMB_ITEM_TAG_NAME, BREADCRUMB_TAG_NAME } from "./constants";

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [BREADCRUMB_TAG_NAME]: { props: BreadcrumbDataProps; handle: undefined };
  }
  interface InertTagNames {
    [BREADCRUMB_ITEM_TAG_NAME]: true;
  }
}

const RWCBreadcrumbItem = rwc(BREADCRUMB_ITEM_TAG_NAME);

export function readBreadcrumbSlotted(
  el: HTMLElement,
): Partial<BreadcrumbDataProps> {
  const items = Array.from(
    el.querySelectorAll<HTMLElement>(`:scope > ${RWCBreadcrumbItem.tagName}`),
  ).map((item) => ({
    label: item.textContent?.trim() ?? "",
    href: item.getAttribute("href") ?? undefined,
    active: item.hasAttribute("active"),
    icon: item.getAttribute("icon") ?? undefined,
  }));

  return { items };
}

function RWCBreadcrumbComponent({
  items = [],
}: BreadcrumbDataProps): JSX.Element {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        {items.map((item, index) => (
          <Fragment key={`${item.label}-${index}`}>
            {index > 0 && <BreadcrumbSeparator />}
            <BreadcrumbItem>
              <BreadcrumbLink
                href={item.href}
                active={item.active}
                icon={item.icon as IconName | undefined}
              >
                {item.label}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

const RWCBreadcrumb = rwc(BREADCRUMB_TAG_NAME, {
  component: RWCBreadcrumbComponent,
  dataSchema: breadcrumbDataSchema,
  usesSlots: true,
  readSlotted: readBreadcrumbSlotted,
});

componentManager.register(RWCBreadcrumbItem, RWCBreadcrumb);
